import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable, DataTableToolbar } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { toast } from '@/hooks/useToast';

import {
  isTenantAdminAccessDenied,
  useActivateTenantUser,
  useDeactivateTenantUser,
  useProvisionTenantUser,
  useReplaceTenantUserRoles,
  useTenantUsers,
  type ProvisionUserInput,
  type TenantUserViewModel,
} from '../api';
import { AssignTenantUserRolesDialog } from './AssignTenantUserRolesDialog';
import { DeactivateTenantUserConfirmDialog } from './DeactivateTenantUserConfirmDialog';
import { ProvisionTenantUserDialog } from './ProvisionTenantUserDialog';
import { TenantUsersTable } from './TenantUsersTable';

const EMPTY_CELL = '—';
const DEFAULT_PAGE_SIZE = 10;

/**
 * "Users" tab body for the tenant detail view (PTV2-1426, cluster-admin tenant-admin reach). List +
 * search + provision + activate/deactivate + role assignment, all scoped to one tenant via
 * `/tenants/{uuid}/users*`. A 404 from the scoped list means the caller lacks management access to
 * THIS tenant (anti-enumeration) — rendered as an access-denied message, not a generic fetch error.
 */
export function TenantUsersPanel({ tenantUuid }: { readonly tenantUuid: string }): ReactElement {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const debouncedQuery = useDebouncedValue(searchInput, 300);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, size]);

  const usersQuery = useTenantUsers({ tenantUuid, page, size, q: debouncedQuery });
  const provisionUser = useProvisionTenantUser();
  const activateUser = useActivateTenantUser();
  const deactivateUser = useDeactivateTenantUser();
  const replaceRoles = useReplaceTenantUserRoles();

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState<TenantUserViewModel | null>(null);
  const [rolesTarget, setRolesTarget] = useState<TenantUserViewModel | null>(null);
  const [pendingActionGuid, setPendingActionGuid] = useState<string | null>(null);

  const result = usersQuery.data;
  const users = useMemo(() => result?.rows ?? [], [result]);
  const totalElements = result?.totalElements ?? 0;
  const totalPages = result?.totalPages ?? 1;
  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = Math.min(totalElements, from + users.length - 1);

  function handleProvision(input: Omit<ProvisionUserInput, 'tenantUuid'>): void {
    provisionUser.mutate(
      { tenantUuid, ...input },
      {
        onSuccess: () => {
          provisionUser.reset();
          setProvisionOpen(false);
          toast({ title: t('routes.platform.detail.users.success'), variant: 'success' });
        },
      },
    );
  }

  function handleActivate(user: TenantUserViewModel): void {
    setPendingActionGuid(user.uuid);
    activateUser.mutate(
      { tenantUuid, userGuid: user.uuid },
      { onSettled: () => { setPendingActionGuid(null); } },
    );
  }

  function handleDeactivateConfirm(user: TenantUserViewModel): void {
    deactivateUser.mutate(
      { tenantUuid, userGuid: user.uuid },
      {
        onSuccess: () => {
          setPendingDeactivate(null);
          deactivateUser.reset();
        },
      },
    );
  }

  function handleAssignRoles(roles: readonly string[]): void {
    if (rolesTarget === null) return;
    replaceRoles.mutate(
      { tenantUuid, userGuid: rolesTarget.uuid, roles },
      {
        onSuccess: () => {
          setRolesTarget(null);
          replaceRoles.reset();
        },
      },
    );
  }

  if (usersQuery.isError && isTenantAdminAccessDenied(usersQuery.error)) {
    return (
      <p role="alert" className="max-w-xl text-sm text-muted-foreground">
        {t('routes.platform.detail.users.accessDenied')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); }}
            placeholder={t('routes.platform.detail.users.searchPlaceholder')}
            aria-label={t('routes.platform.detail.users.searchAriaLabel')}
            className="pl-9 pr-9"
            autoComplete="off"
            spellCheck={false}
          />
          {searchInput.length > 0 && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); }}
              aria-label={t('routes.platform.detail.users.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => {
            provisionUser.reset();
            setProvisionOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('routes.platform.detail.users.provisionAction')}
        </Button>
      </div>

      {usersQuery.isPending && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-md" />
          ))}
        </div>
      )}

      {usersQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={usersQuery.error}
            heading={t('routes.platform.detail.users.error.heading')}
            message={t('routes.platform.detail.users.error.message')}
            codeLabel={t('routes.platform.detail.users.error.codeLabel')}
            onRetry={() => { usersQuery.refetch().catch(() => undefined); }}
            retryLabel={t('routes.platform.detail.users.error.retry')}
          />
        </div>
      )}

      {usersQuery.isSuccess && users.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {debouncedQuery.trim().length > 0
            ? t('routes.platform.detail.users.searchEmpty', { query: debouncedQuery })
            : t('routes.platform.detail.users.empty')}
        </p>
      )}

      {usersQuery.isSuccess && users.length > 0 && (
        <DataTable aria-label={t('routes.platform.detail.users.table.ariaLabel')} className="flex flex-col">
          <DataTableToolbar
            pageSize={size}
            onPageSizeChange={setSize}
            from={from}
            to={to}
            total={totalElements}
            onPagePrevious={() => { setPage((p) => Math.max(0, p - 1)); }}
            onPageNext={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); }}
            canGoPrevious={page > 0}
            canGoNext={page < totalPages - 1}
            pageSizeSelectId="tenant-users-page-size"
          />
          <TenantUsersTable
            users={users}
            emptyCell={EMPTY_CELL}
            onActivate={handleActivate}
            onDeactivate={setPendingDeactivate}
            onManageRoles={setRolesTarget}
            pendingUserGuid={pendingActionGuid}
          />
        </DataTable>
      )}

      <ProvisionTenantUserDialog
        open={provisionOpen}
        tenantUuid={tenantUuid}
        onOpenChange={(next) => {
          if (!next) provisionUser.reset();
          setProvisionOpen(next);
        }}
        onSubmit={handleProvision}
        pending={provisionUser.isPending}
        submitError={provisionUser.error}
      />

      <DeactivateTenantUserConfirmDialog
        user={pendingDeactivate}
        onCancel={() => {
          if (deactivateUser.isPending) return;
          setPendingDeactivate(null);
          deactivateUser.reset();
        }}
        onConfirm={handleDeactivateConfirm}
        pending={deactivateUser.isPending}
        submitError={deactivateUser.error}
      />

      <AssignTenantUserRolesDialog
        tenantUuid={tenantUuid}
        user={rolesTarget}
        onCancel={() => {
          if (replaceRoles.isPending) return;
          setRolesTarget(null);
          replaceRoles.reset();
        }}
        onConfirm={handleAssignRoles}
        pending={replaceRoles.isPending}
        submitError={replaceRoles.error}
      />
    </div>
  );
}
